import styles from 'css/src/components/app/chat-tab/components/SubagentsModal.module.scss';
import React from 'react';

import { watchConversationInStepsPanel } from 'js/src/agent/steps-panel/stepsPanelStore';
import { conversationDetailUrl } from 'js/src/agent/subagentConversations';
import type { SubagentRegistry, SubagentRun } from 'js/src/agent/subagents/SubagentRegistry';
import { SubagentStatus } from 'js/src/agent/subagents/SubagentRegistry';
import { useSubagentRuns } from 'js/src/agent/subagents/useSubagentRuns';
import {
    BUTTON_SIZES,
    DangerSecondaryButton,
    TertiaryIconButton,
} from 'js/src/components/common/ui/Button';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';

interface SubagentsModalProps {
    showing: boolean;
    onHide: VoidFunction;
    registry: SubagentRegistry;
}

const STATUS_LABELS: Record<SubagentStatus, string> = {
    [SubagentStatus.QUEUED]: 'waiting',
    [SubagentStatus.RUNNING]: 'running',
    [SubagentStatus.COMPLETED]: 'done',
    [SubagentStatus.FAILED]: 'failed',
    [SubagentStatus.KILLED]: 'killed',
};

function useNow(isTicking: boolean): number {
    const [now, setNow] = React.useState(() => Date.now());
    React.useEffect(() => {
        if (!isTicking) {
            return undefined;
        }
        const interval = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(interval);
    }, [isTicking]);
    return now;
}

function formatElapsed(run: SubagentRun, now: number): string {
    const since = run.startedAt ?? run.queuedAt;
    const seconds = Math.max(0, Math.round(((run.endedAt ?? now) - since) / 1000));
    return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

const isActive = (run: SubagentRun): boolean =>
    run.status === SubagentStatus.RUNNING || run.status === SubagentStatus.QUEUED;

function statusLabel(run: SubagentRun): string {
    if (isActive(run) && run.killRequested) {
        return 'stopping';
    }
    return STATUS_LABELS[run.status];
}

function placeInLine(position: number): string {
    if (position === 1) {
        return 'next up';
    }
    const suffix = { 2: 'nd', 3: 'rd' }[position % 10] ?? 'th';
    const ordinal = position % 100 >= 11 && position % 100 <= 13 ? 'th' : suffix;
    return `${position}${ordinal} in line`;
}

interface SubagentRowProps {
    run: SubagentRun;
    now: number;
    positionInLine?: number;
    registry: SubagentRegistry;
    onHide: VoidFunction;
}

const SubagentRow: React.FC<SubagentRowProps> = ({
    run,
    now,
    positionInLine,
    registry,
    onHide,
}) => {
    const isWaiting = run.status === SubagentStatus.QUEUED;
    return (
        <li
            className={`d-flex align-items-center justify-content-between gap-2 border rounded-3 p-l5-12 ${isWaiting ? styles['waiting-row'] : ''}`}
        >
            <div className="d-flex flex-column overflow-hidden">
                <span className="d-flex align-items-center gap-2 fw-semibold text-truncate">
                    {run.status === SubagentStatus.RUNNING && !run.killRequested && (
                        <span className={`${styles['running-dot']} flex-shrink-0`} aria-hidden />
                    )}
                    {run.description}
                </span>
                <span className="font-monospace color-grey-500 small">
                    {run.type} · {run.conversation.model} · {statusLabel(run)}{' '}
                    {formatElapsed(run, now)}
                    {positionInLine !== undefined && ` · ${placeInLine(positionInLine)}`}
                </span>
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <TertiaryIconButton
                    icon="visibility"
                    tooltipTitle="Watch in the steps panel"
                    aria-label={`Watch ${run.description}`}
                    onClick={() => {
                        watchConversationInStepsPanel(run.conversation);
                        onHide();
                    }}
                />
                {run.conversation.isPersisted() && (
                    <a
                        href={conversationDetailUrl(run.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open conversation for ${run.description}`}
                        className="color-grey-500 d-flex align-items-center"
                    >
                        <i translate="no" className="material-symbols-outlined" aria-hidden>
                            open_in_new
                        </i>
                    </a>
                )}
                {isActive(run) && (
                    <DangerSecondaryButton
                        text="Kill"
                        size={BUTTON_SIZES.SM}
                        disabled={run.killRequested}
                        aria-label={`Kill ${run.description}`}
                        onClick={() => registry.kill(run.id)}
                    />
                )}
            </div>
        </li>
    );
};

interface SubagentSectionProps {
    title: string;
    children: React.ReactNode;
}

const SubagentSection: React.FC<SubagentSectionProps> = ({ title, children }) => {
    const headingId = React.useId();
    return (
        <section>
            <h6 id={headingId} className="text-uppercase color-grey-500 small fw-semibold mb-l5-8">
                {title}
            </h6>
            <ul
                aria-labelledby={headingId}
                className="list-unstyled d-flex flex-column gap-2 mb-l5-0"
            >
                {children}
            </ul>
        </section>
    );
};

const SubagentsModal: React.FC<SubagentsModalProps> = ({ showing, onHide, registry }) => {
    const runs = useSubagentRuns(registry);
    const isAnyRunning = runs.some(isActive);
    const now = useNow(showing && isAnyRunning);
    const running = runs.filter((run) => run.status === SubagentStatus.RUNNING);
    const waiting = runs.filter((run) => run.status === SubagentStatus.QUEUED);
    const finishedNewestFirst = runs.filter((run) => !isActive(run)).reverse();
    const inLine = waiting.filter((run) => !run.killRequested);
    const rowProps = { now, registry, onHide };

    return (
        <Modal
            showing={showing}
            onHidden={onHide}
            title={`Subagents (${registry.getRunningCount()}/${registry.getMaxConcurrent()} running${registry.getQueuedCount() > 0 ? `, ${registry.getQueuedCount()} waiting` : ''})`}
            size={MODAL_SIZES.DEFAULT}
            scrollable
        >
            {runs.length === 0 && (
                <div className="text-muted text-center py-l5-24">No subagents yet</div>
            )}
            <div className="d-flex flex-column gap-4">
                {running.length > 0 && (
                    <SubagentSection title="Running">
                        {running.map((run) => (
                            <SubagentRow key={run.id} run={run} {...rowProps} />
                        ))}
                    </SubagentSection>
                )}
                {waiting.length > 0 && (
                    <SubagentSection title="Waiting">
                        {waiting.map((run) => {
                            const position = inLine.indexOf(run) + 1;
                            return (
                                <SubagentRow
                                    key={run.id}
                                    run={run}
                                    positionInLine={position > 0 ? position : undefined}
                                    {...rowProps}
                                />
                            );
                        })}
                    </SubagentSection>
                )}
                {finishedNewestFirst.length > 0 && (
                    <SubagentSection title="Finished">
                        {finishedNewestFirst.map((run) => (
                            <SubagentRow key={run.id} run={run} {...rowProps} />
                        ))}
                    </SubagentSection>
                )}
            </div>
        </Modal>
    );
};

export default SubagentsModal;
